/**
 * DeliveryInfoCard Component
 * Displays delivery address, contact person, dates, and special instructions
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-004
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, User, Phone, Calendar, FileText, Truck } from 'lucide-react';
import type { DeliveryInfo } from '@/types/order';

interface DeliveryInfoCardProps {
  delivery: DeliveryInfo;
}

/**
 * Format date to readable format
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function DeliveryInfoCard({ delivery }: DeliveryInfoCardProps) {
  const { address, contactPerson, contactPhone, expectedDeliveryDate, actualDeliveryDate, specialInstructions } = delivery;

  const fullAddress = [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.pincode,
    address.country,
  ]
    .filter(Boolean)
    .join(', ');

  const isDelivered = Boolean(actualDeliveryDate);

  return (
    <Card className="border-slate-200 bg-white shadow-md">
      <CardHeader className="pb-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl text-slate-900 flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            Delivery Information
          </CardTitle>
          {isDelivered && (
            <Badge variant="default" className="bg-green-600">
              Delivered
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        {/* Delivery Address */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
            <MapPin className="h-4 w-4 text-red-600" />
            Delivery Address
          </h3>
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-900 leading-relaxed">{fullAddress}</p>
            {address.landmark && (
              <p className="text-xs text-slate-500 mt-2">
                Landmark: {address.landmark}
              </p>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <User className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-blue-700 font-medium">Contact Person</p>
              <p className="text-sm font-semibold text-blue-900 truncate">
                {contactPerson}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
            <Phone className="h-5 w-5 text-green-600 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-green-700 font-medium">Phone</p>
              <p className="text-sm font-semibold text-green-900 truncate">
                {contactPhone}
              </p>
            </div>
          </div>
        </div>

        {/* Delivery Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <Calendar className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-amber-700 font-medium">Expected Delivery</p>
              <p className="text-sm font-semibold text-amber-900">
                {formatDate(expectedDeliveryDate)}
              </p>
            </div>
          </div>
          {actualDeliveryDate && (
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <Calendar className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-green-700 font-medium">Actual Delivery</p>
                <p className="text-sm font-semibold text-green-900">
                  {formatDate(actualDeliveryDate)}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Special Instructions */}
        {specialInstructions && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
              <FileText className="h-4 w-4 text-purple-600" />
              Special Instructions
            </h3>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-900 leading-relaxed">
                {specialInstructions}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
